/**
 * Whole-house broadcast roster safety check — pure core shared by the server
 * (pre-send check + send-time enforcement + safety sweeps) and the composer.
 *
 * A broadcast only reaches members PadPulse already has on record for a house.
 * This module compares a house's bedrooms with its current members and returns
 * every reason a resident might be missed, plus a stable fingerprint of those
 * gaps so the send route can refuse a blast whose gaps changed after the
 * operator acknowledged them.
 *
 * Signal choices were confirmed against production data:
 *  - `rooms.status` `occupied` / `moving_in` are DERIVED from members by the
 *    reconcile-room-statuses job, so an occupied-status bedroom with no current
 *    member means the member link was lost after the last reconcile.
 *  - `rooms.status` `inactive` is PadSplit's "unlisted" listing state. At a
 *    house with residents an unlisted bedroom with nobody assigned is the shape
 *    of a missed occupant (PadSplit unlists rooms while they are filled). Rooms
 *    the harvest has not seen for a week are treated as removed, not unlisted.
 *  - Operational statuses (`needs_flip`, `waiting_for_approval`, maintenance)
 *    and `vacant` are not occupancy signals.
 *  - Member harvest recency is `COALESCE(last_harvested, updated_at)`; the
 *    harvest refreshes every present member at least every ~12h, so a house
 *    whose freshest current member is older than 24h is stale.
 */

export const ROSTER_STALE_AFTER_MS = 24 * 60 * 60 * 1000;
export const UNLISTED_ROOM_FRESH_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;
/** A recipient still unconfirmed this long after the send time is incomplete. */
export const BROADCAST_DELIVERY_GRACE_MS = 15 * 60 * 1000;
/**
 * How long after a broadcast's send time the missed-resident safety net keeps
 * watching its houses. A resident who was living there at send time but missing
 * from PadPulse's roster is sent the same message automatically (the sweep runs
 * every minute); someone who moved in afterwards raises a Team Inbox item with a
 * one-click send instead.
 */
export const BROADCAST_ROSTER_GAP_WINDOW_MS = 24 * 60 * 60 * 1000;
/**
 * Send-time confirmation: a house counts as confirmed with PadSplit once its
 * member data was harvested no earlier than this long before Send was pressed.
 */
export const ROSTER_CONFIRM_FRESH_WITHIN_MS = 10 * 60 * 1000;
/** Longest the composer waits for PadSplit to confirm every house. */
export const ROSTER_CONFIRM_TIMEOUT_MS = 15 * 60 * 1000;
export type BroadcastAudienceMode = "whole_house" | "selected_residents";
export interface RosterConfirmation {
  state: "checking" | "confirmed" | "unavailable" | "failed";
  message: string;
}

export const OCCUPIED_ROOM_STATUSES = ["occupied", "moving_in"] as const;
export const UNLISTED_ROOM_STATUSES = ["inactive"] as const;

export type RosterGapKind =
  | "occupied_room_no_member"
  | "unlisted_room_no_member"
  | "property_conflict"
  | "duplicate_room"
  | "missing_host_link"
  | "omitted_member"
  | "stale_data"
  | "no_member_data";

export interface RosterGap {
  kind: RosterGapKind;
  propertyId: number;
  roomId: number | null;
  roomNumber: string | null;
  memberId: number | null;
  memberName: string | null;
  detail: string;
}

export interface PropertyRosterCheck {
  propertyId: number;
  propertyName: string | null;
  bedroomCount: number;
  /** Members who would receive this house's broadcast if all were selected. */
  eligibleMemberIds: number[];
  /** Eligible members actually in the recipient list (null selection → all). */
  recipientMemberIds: number[];
  /** Freshest harvest stamp among this house's current members (ISO) or null. */
  lastHarvestedAt: string | null;
  /**
   * Send-time confirmation evidence: the OLDEST PadSplit harvest stamp among
   * the members who would receive this house's broadcast (null when any of
   * them has never been harvested, or nobody is eligible). A house is only
   * confirmed once every eligible resident was re-seen by PadSplit, so one
   * recently touched row — or a conflicted/unlinked member — cannot vouch for
   * an otherwise unharvested house.
   */
  confirmedHarvestAt: string | null;
  stale: boolean;
  gaps: RosterGap[];
}

export interface AutoIncludedMember {
  memberId: number;
  memberName: string;
  propertyId: number;
  roomNumber: string | null;
}

export interface RosterCheckResult {
  audienceMode?: BroadcastAudienceMode;
  confirmation?: RosterConfirmation;
  checkedAt: string;
  properties: PropertyRosterCheck[];
  gapCount: number;
  fingerprint: string;
  /**
   * Current residents the sender's recipient list did not know about (neither
   * selected nor explicitly left out). They are added to the broadcast
   * automatically, so a resident synced after the composer loaded is never
   * silently skipped. Not part of the fingerprint.
   */
  autoIncludedMembers?: AutoIncludedMember[];
}

export interface RosterBedroomInput {
  id: number;
  padsplitRoomId?: string | null;
  roomNumber: string | null;
  status: string | null;
  /** COALESCE(last_harvested, updated_at) for the room row. */
  freshAt: Date | null;
}

export interface RosterCandidateInput {
  id: number;
  type?: string | null;
  localTenancyVerified?: boolean;
  occupantId?: string | null;
  padsplitMemberId?: string | null;
  name: string;
  /** Billable bedroom id at THIS property, or null. */
  roomId: number | null;
  roomNumber: string | null;
  /** Output of resolveMemberPropertyLink for this member. */
  resolvedPropertyId: number | null;
  conflict: boolean;
  conflictingPropertyId: number | null;
  /** Linked (members_users) to a host the caller can act for who owns this house. */
  hostLinked: boolean;
  freshAt: Date | null;
  /**
   * PadSplit harvest stamp only (members.last_harvested, no updated_at
   * fallback). The only evidence that counts toward send-time confirmation —
   * a local edit bumps updated_at without PadSplit having seen anyone.
   */
  harvestedAt?: Date | null;
}

export interface PropertyRosterInput {
  propertyId: number;
  padsplitPropertyId?: string | null;
  propertyName: string | null;
  bedrooms: RosterBedroomInput[];
  /**
   * Current (not booking-request / inactive) members with ANY signal touching
   * this property: tenancy, room, or a conflicting link.
   */
  candidates: RosterCandidateInput[];
}

function roomLabel(roomNumber: string | null): string {
  if (!roomNumber) return "A bedroom";
  return /^room\b/i.test(roomNumber.trim()) ? roomNumber.trim() : `Room ${roomNumber.trim()}`;
}

/** Compute one house's roster check. `selected` null = no recipient list yet. */
export function computePropertyRosterCheck(
  input: PropertyRosterInput,
  selected: ReadonlySet<number> | null,
  now: Date,
  excluded: ReadonlySet<number> = new Set(),
): PropertyRosterCheck {
  const pid = input.propertyId;
  const gaps: RosterGap[] = [];
  const eligible: RosterCandidateInput[] = [];
  const roomsWithAnyCandidate = new Set<number>();

  for (const c of input.candidates) {
    if (c.roomId != null) roomsWithAnyCandidate.add(c.roomId);
    if (excluded.has(c.id) && (c.conflict || !c.hostLinked)) continue;
    if (c.conflict) {
      gaps.push({
        kind: "property_conflict",
        propertyId: pid,
        roomId: c.roomId,
        roomNumber: c.roomNumber,
        memberId: c.id,
        memberName: c.name,
        detail: `${c.name} has a tenancy and a room at different properties, so they are left out until fixed on the Members page.`,
      });
      continue;
    }
    if (c.resolvedPropertyId !== pid) continue;
    if (!c.hostLinked) {
      gaps.push({
        kind: "missing_host_link",
        propertyId: pid,
        roomId: c.roomId,
        roomNumber: c.roomNumber,
        memberId: c.id,
        memberName: c.name,
        detail: `${c.name} lives here but is not linked to a host account that owns this house, so they cannot be messaged from it.`,
      });
      continue;
    }
    eligible.push(c);
  }

  const occupants = new Map<number, RosterCandidateInput[]>();
  for (const e of eligible) {
    if (e.roomId == null) continue;
    const list = occupants.get(e.roomId);
    if (list) list.push(e);
    else occupants.set(e.roomId, [e]);
  }
  for (const [roomId, list] of occupants) {
    if (list.length < 2) continue;
    const sorted = [...list].sort((a, b) => a.id - b.id);
    gaps.push({
      kind: "duplicate_room",
      propertyId: pid,
      roomId,
      roomNumber: sorted[0].roomNumber,
      memberId: null,
      memberName: sorted.map((m) => m.name).join(", "),
      detail: `${roomLabel(sorted[0].roomNumber)} has ${sorted.length} members assigned (${sorted.map((m) => m.name).join(", ")}). One may belong to another house.`,
    });
  }

  for (const room of input.bedrooms) {
    if (occupants.has(room.id) || roomsWithAnyCandidate.has(room.id)) continue;
    const status = (room.status ?? "").toLowerCase().trim();
    if ((OCCUPIED_ROOM_STATUSES as readonly string[]).includes(status)) {
      gaps.push({
        kind: "occupied_room_no_member",
        propertyId: pid,
        roomId: room.id,
        roomNumber: room.roomNumber,
        memberId: null,
        memberName: null,
        detail: `${roomLabel(room.roomNumber)} looks occupied but has no member in PadPulse.`,
      });
    } else if (
      (UNLISTED_ROOM_STATUSES as readonly string[]).includes(status) &&
      eligible.length > 0 &&
      room.freshAt != null &&
      now.getTime() - room.freshAt.getTime() <= UNLISTED_ROOM_FRESH_WITHIN_MS
    ) {
      gaps.push({
        kind: "unlisted_room_no_member",
        propertyId: pid,
        roomId: room.id,
        roomNumber: room.roomNumber,
        memberId: null,
        memberName: null,
        detail: `${roomLabel(room.roomNumber)} is unlisted on PadSplit with nobody assigned in PadPulse — it may be occupied.`,
      });
    }
  }

  if (selected) {
    for (const e of eligible) {
      if (selected.has(e.id) || excluded.has(e.id)) continue;
      gaps.push({
        kind: "omitted_member",
        propertyId: pid,
        roomId: e.roomId,
        roomNumber: e.roomNumber,
        memberId: e.id,
        memberName: e.name,
        detail: `${e.name} is a current member but is not selected for this broadcast.`,
      });
    }
  }

  let freshest: Date | null = null;
  for (const c of input.candidates) {
    if (c.freshAt && (!freshest || c.freshAt > freshest)) freshest = c.freshAt;
  }
  let stale = false;
  if (input.candidates.length === 0) {
    stale = true;
    gaps.push({
      kind: "no_member_data",
      propertyId: pid,
      roomId: null,
      roomNumber: null,
      memberId: null,
      memberName: null,
      detail: "PadPulse has no current members for this house. Refresh from PadSplit before sending.",
    });
  } else if (!freshest || now.getTime() - freshest.getTime() > ROSTER_STALE_AFTER_MS) {
    stale = true;
    gaps.push({
      kind: "stale_data",
      propertyId: pid,
      roomId: null,
      roomNumber: null,
      memberId: null,
      memberName: null,
      detail: freshest
        ? `Member data for this house was last refreshed from PadSplit ${Math.round((now.getTime() - freshest.getTime()) / 3_600_000)}h ago.`
        : "Member data for this house has no PadSplit refresh time.",
    });
  }

  return {
    propertyId: pid,
    propertyName: input.propertyName,
    bedroomCount: input.bedrooms.length,
    eligibleMemberIds: eligible.map((e) => e.id).sort((a, b) => a - b),
    recipientMemberIds: eligible
      .filter((e) => !selected || selected.has(e.id))
      .map((e) => e.id)
      .sort((a, b) => a - b),
    lastHarvestedAt: freshest ? freshest.toISOString() : null,
    confirmedHarvestAt: oldestEligibleHarvest(eligible.filter(e => !excluded.has(e.id))),
    stale,
    gaps,
  };
}

/**
 * Residents eligible at a house whom the sender neither selected nor explicitly
 * left out. Members sharing a bedroom (duplicate_room) are not auto-added: one
 * of them may belong to another house, so that stays a reviewed gap.
 */
export function findAutoIncludedMembers(
  inputs: PropertyRosterInput[],
  baseline: PropertyRosterCheck[],
  selected: ReadonlySet<number>,
  excluded: ReadonlySet<number>,
): AutoIncludedMember[] {
  const out: AutoIncludedMember[] = [];
  const seen = new Set<number>();
  for (const check of baseline) {
    const input = inputs.find((i) => i.propertyId === check.propertyId);
    if (!input) continue;
    const dupRooms = new Set(
      check.gaps.filter((g) => g.kind === "duplicate_room" && g.roomId != null).map((g) => g.roomId as number),
    );
    for (const id of check.eligibleMemberIds) {
      if (selected.has(id) || excluded.has(id) || seen.has(id)) continue;
      const c = input.candidates.find((x) => x.id === id);
      if (!c || (c.roomId != null && dupRooms.has(c.roomId))) continue;
      seen.add(id);
      out.push({ memberId: id, memberName: c.name, propertyId: check.propertyId, roomNumber: c.roomNumber });
    }
  }
  return out;
}

function oldestEligibleHarvest(eligible: RosterCandidateInput[]): string | null {
  if (eligible.length === 0) return null;
  let oldest: Date | null = null;
  for (const e of eligible) {
    const h = e.harvestedAt ?? null;
    if (!h || Number.isNaN(h.getTime())) return null;
    if (!oldest || h < oldest) oldest = h;
  }
  return oldest ? oldest.toISOString() : null;
}

/**
 * True once a house counts as confirmed with PadSplit at `pressedAt`: every
 * eligible resident's PadSplit harvest stamp (see `confirmedHarvestAt`) is no
 * older than ROSTER_CONFIRM_FRESH_WITHIN_MS before Send was pressed.
 */
export function isHouseConfirmed(confirmedHarvestAt: string | null | undefined, pressedAt: number): boolean {
  if (!confirmedHarvestAt) return false;
  const t = new Date(confirmedHarvestAt).getTime();
  return Number.isFinite(t) && t <= pressedAt && t >= pressedAt - ROSTER_CONFIRM_FRESH_WITHIN_MS;
}

/** Selected audiences prove only the intended identities, never infer a whole roster. */
export function computeSelectedRosterChecks(
  inputs: PropertyRosterInput[], selected: ReadonlySet<number>, now: Date,
): PropertyRosterCheck[] {
  const checks = inputs.map(input => {
    const candidates = input.candidates.filter(c => selected.has(c.id));
    // Keep room peers for duplicate identity detection, but not unrelated gaps.
    const selectedRooms = new Set(candidates.flatMap(c => c.roomId == null ? [] : [c.roomId]));
    const baseline = computePropertyRosterCheck({ ...input, bedrooms: [], candidates: input.candidates.filter(c => selected.has(c.id) || (c.roomId != null && selectedRooms.has(c.roomId))) }, null, now);
    baseline.gaps = baseline.gaps.filter(g => g.kind === "duplicate_room" || (g.memberId != null && selected.has(g.memberId)));
    baseline.eligibleMemberIds = baseline.eligibleMemberIds.filter(id => selected.has(id));
    baseline.recipientMemberIds = baseline.eligibleMemberIds;
    baseline.confirmedHarvestAt = oldestEligibleHarvest(candidates.filter(c => baseline.eligibleMemberIds.includes(c.id)));
    baseline.stale = !isHouseConfirmed(baseline.confirmedHarvestAt, now.getTime());
    if (candidates.length && baseline.stale) baseline.gaps.push({
      kind: "stale_data", propertyId: input.propertyId, roomId: null, roomNumber: null,
      memberId: null, memberName: null, detail: "Selected residents need fresh PadSplit identity and membership evidence. Refresh this house before sending.",
    });
    return baseline;
  }).filter(c => c.recipientMemberIds.length || c.gaps.length);
  const found = new Set(checks.flatMap(c => c.recipientMemberIds));
  for (const id of selected) {
    if (found.has(id) || checks.some(c => c.gaps.some(g => g.memberId === id))) continue;
    const check = checks[0] ?? (inputs[0] ? { ...computePropertyRosterCheck({ ...inputs[0], bedrooms: [], candidates: [] }, null, now), gaps: [] } : null);
    if (!check) continue;
    if (!checks.includes(check)) checks.push(check);
    check.gaps.push({ kind: "missing_host_link", propertyId: check.propertyId, roomId: null, roomNumber: null, memberId: id, memberName: null,
      detail: `Selected member #${id} is not a verified current resident of an authorized house. Refresh or correct their membership before sending.` });
  }
  for (const check of checks) for (const id of check.recipientMemberIds) {
    const c = inputs.find(i => i.propertyId === check.propertyId)?.candidates.find(c => c.id === id);
    if (c && c.type !== "direct" && !(c.occupantId || c.padsplitMemberId)) check.gaps.push({
      kind: "missing_host_link", propertyId: check.propertyId, roomId: c.roomId, roomNumber: c.roomNumber,
      memberId: c.id, memberName: c.name, detail: "Selected resident has no stable PadSplit identity. Refresh or correct this resident before sending.",
    });
  }
  return checks;
}

/** Consent is review metadata, never evidence of identity, completeness or freshness. */
export function isBroadcastRosterReady(check: RosterCheckResult): boolean {
  return check.confirmation?.state === "confirmed" && check.gapCount === 0 && check.properties.length > 0;
}

/** Stable identity of a gap — excludes wording and timestamps. */
export function rosterGapKey(g: RosterGap): string {
  return `${g.kind}:${g.propertyId}:${g.roomId ?? ""}:${g.memberId ?? ""}`;
}

function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/**
 * Fingerprint of the gap SET across every checked house. Two checks agree iff
 * they found the same gaps (same kind/house/room/member). Ordering, wording,
 * and refresh timestamps do not affect it.
 */
export function rosterFingerprint(properties: PropertyRosterCheck[]): string {
  const keys = properties.flatMap((p) => p.gaps.map(rosterGapKey)).sort();
  const pids = properties.map((p) => p.propertyId).sort((a, b) => a - b);
  return `v1-${keys.length}-${fnv1a(`${pids.join(",")}|${keys.join("|")}`)}`;
}

export function buildRosterCheckResult(properties: PropertyRosterCheck[], now: Date): RosterCheckResult {
  return {
    checkedAt: now.toISOString(),
    properties,
    gapCount: properties.reduce((n, p) => n + p.gaps.length, 0),
    fingerprint: rosterFingerprint(properties),
  };
}

/** Gaps present in `current` but absent from `acknowledged` (by gap key). */
export function newRosterGaps(current: RosterCheckResult, acknowledgedKeys: ReadonlySet<string>): RosterGap[] {
  return current.properties.flatMap((p) => p.gaps).filter((g) => !acknowledgedKeys.has(rosterGapKey(g)));
}

// ─── Delivery tracking ───────────────────────────────────────────────────────

export type BroadcastRecipientChannel = "padsplit" | "quo" | "direct";
export type BroadcastRecipientState = "queued" | "handed_off" | "confirmed" | "failed" | "cancelled";

export interface RecipientDeliveryFields {
  channel: BroadcastRecipientChannel;
  sentAt: Date | string | null;
  sendError: string | null;
  cancelledAt: Date | string | null;
  reconciledChatId: number | null;
  providerMessageId: string | null;
}

/**
 * Map a recipient's messages_pending row to a delivery state.
 *  - PadSplit: `sent_at` (stamped by the upstream send job) only means the
 *    message was HANDED to PadSplit; confirmation requires the harvest to have
 *    reconciled it into the member's thread (`reconciled_chat_id`).
 *  - Quo: the provider message id is the delivery receipt.
 *  - Direct (no external thread): the PadPulse thread row is the record, so a
 *    stamped `sent_at` is confirmation.
 *  - `cancelled_at` with a `send_error` is an exhausted (failed) row; without
 *    one it was cancelled by an operator (e.g. a cancelled scheduled send).
 */
export function classifyRecipientDelivery(f: RecipientDeliveryFields): BroadcastRecipientState {
  if (f.reconciledChatId != null) return "confirmed";
  if (f.cancelledAt != null && !f.sendError) return "cancelled";
  if (f.sendError || f.cancelledAt != null) return "failed";
  if (f.channel === "quo" && f.providerMessageId) return "confirmed";
  if (f.channel === "direct" && f.sentAt != null) return "confirmed";
  if (f.sentAt != null) return "handed_off";
  return "queued";
}

export const BROADCAST_RECIPIENT_STATE_LABELS: Record<BroadcastRecipientState, string> = {
  queued: "Queued",
  handed_off: "Handed to PadSplit",
  confirmed: "Confirmed in thread",
  failed: "Failed",
  cancelled: "Cancelled",
};
